import { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, Trophy, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, fieldInputCls } from "@/components/Field";
import { PageHeader } from "@/pages/Events";
import { TelemetryAnalysis } from "@/components/telemetry/TelemetryAnalysis";
import { LapPicker, useCatalog, useRemoteLap } from "@/components/telemetry/LapPicker";
import { EventMultiSelect } from "@/components/telemetry/EventMultiSelect";
import { useT } from "@/lib/i18n";
import { fmtLap } from "@/lib/telemetry";

const bestOf = (list) => {
  const it = list.reduce((m, i) => (i.best != null && (!m || i.best < m.best) ? i : m), null);
  return it ? { it, lap: it.laps.reduce((m, l) => (!m || l.time < m.time ? l : m), null) } : null;
};

const Pick = ({ label, value, onChange, options, testid }) => (
  <Field label={label}>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`${fieldInputCls} [&>span]:truncate`}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </Field>
);

export default function TelemetryCompare() {
  const { t } = useT();
  const all = useCatalog(null);
  const [trackId, setTrackId] = useState(null);
  const [driver, setDriver] = useState("all");
  const [eventIds, setEventIds] = useState([]);
  const [selA, setSelA] = useState(null);
  const [selB, setSelB] = useState(null);
  const [selC, setSelC] = useState(null);
  const tracks = useMemo(() => {
    const m = new Map();
    (all || []).forEach((i) => m.set(i.track_id, { name: i.track_name, n: (m.get(i.track_id)?.n || 0) + 1 }));
    return [...m.entries()].map(([id, v]) => [id, `${v.name} (${v.n})`]);
  }, [all]);
  useEffect(() => {
    if (!trackId && tracks.length) {
      setTrackId(tracks[0][0]);
    }
  }, [tracks, trackId]);
  const onTrack = (id) => { setTrackId(id); setDriver("all"); setEventIds([]); setSelA(null); setSelB(null); setSelC(null); };
  const ofTrack = (all || []).filter((i) => i.track_id === trackId);
  const events = useMemo(() => {
    const m = new Map();
    ofTrack.forEach((i) => m.set(i.event_id, { name: i.event_name, date: i.date, n: (m.get(i.event_id)?.n || 0) + 1 }));
    return [...m.entries()].sort((a, b) => (b[1].date || "").localeCompare(a[1].date || "")).map(([id, v]) => [id, v.name, v.n]);
  }, [ofTrack]);
  const ofEvents = ofTrack.filter((i) => !eventIds.length || eventIds.includes(i.event_id));
  const drivers = [...new Set(ofEvents.map((i) => i.driver).filter(Boolean))].sort();
  const items = ofEvents.filter((i) => driver === "all" || i.driver === driver);
  const lapA = useRemoteLap(selA, all);
  const lapB = useRemoteLap(selB, all);
  const lapC = useRemoteLap(selC, all);
  const swap = () => { setSelA(selB); setSelB(selA); };
  const best = bestOf(items);
  const bestItem = best?.it;
  const bestLap = best?.lap;
  const driverBests = drivers.map((d) => [d, bestOf(ofEvents.filter((i) => i.driver === d))]).filter(([, b]) => b?.lap);
  const curDriverB = driverBests.find(([, b]) => selB?.telId === b.it.id && selB?.lap === b.lap.lap)?.[0];
  const loadDriverBest = (d) => { const b = driverBests.find(([n]) => n === d)?.[1]; if (b) setSelB({ telId: b.it.id, lap: b.lap.lap }); };
  const itemsFor = (sel) => {
    const extra = sel && !items.some((i) => i.id === sel.telId) ? ofTrack.find((i) => i.id === sel.telId) : null;
    return extra ? [...items, extra] : items;
  };
  const isBestA = bestLap && selA?.telId === bestItem.id && selA?.lap === bestLap.lap;
  const loadBest = () => setSelA({ telId: bestItem.id, lap: bestLap.lap });

  return (
    <div>
      <PageHeader title={t("telemetry_compare_title")} sub={t("telemetry_compare_sub")} image="/images/grid.jpg" imagePos="center 60%" />
      {all && !all.length && <div data-testid="telemetry-compare-empty" className="border border-dashed border-[#282D3D] rounded-sm p-10 text-center text-slate-500 font-mono text-sm">{t("telemetry_compare_empty")}</div>}
      {tracks.length > 0 && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Pick label={t("track")} value={trackId} onChange={onTrack} options={tracks} testid="compare-track-select" />
            <EventMultiSelect events={events} value={eventIds} onChange={(v) => { setEventIds(v); setDriver("all"); }} />
            <Pick label={t("driver")} value={driver} onChange={setDriver} options={[["all", t("all_drivers")], ...drivers.map((d) => [d, d])]} testid="compare-driver-select" />
            <button type="button" data-testid="compare-best-a-btn" disabled={!bestLap || isBestA} onClick={loadBest}
              title={bestItem ? `${bestItem.session_name} · ${bestLap?.time?.toFixed(3)}` : ""}
              className="self-end h-10 px-4 flex items-center justify-center gap-2 rounded-sm border border-[#10B981]/60 bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20 disabled:opacity-40 disabled:cursor-not-allowed font-heading uppercase tracking-wide text-base transition-colors duration-200">
              <Trophy className="w-4 h-4" /> {t(isBestA ? "best_loaded" : "load_best_a")}
            </button>
            <Select value={curDriverB || ""} onValueChange={(d) => d && loadDriverBest(d)} disabled={!driverBests.length}>
              <SelectTrigger data-testid="compare-driver-best-b-select" className="self-end h-10 rounded-sm border border-[#FF3B30]/60 bg-[#FF3B30]/10 text-[#FF3B30] font-heading uppercase tracking-wide text-base [&>span]:truncate disabled:opacity-40">
                <SelectValue placeholder={t("driver_best_b")} />
              </SelectTrigger>
              <SelectContent>
                {driverBests.map(([d, b]) => (
                  <SelectItem key={d} value={d} data-testid={`compare-driver-best-b-${d}`}>
                    <span className="font-mono text-sm">{d} · {fmtLap(b.lap.time)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_minmax(0,1fr)] gap-4 items-end bg-[#12141C] border border-[#282D3D] rounded-sm p-4">
            <LapPicker items={itemsFor(selA)} value={selA} onChange={setSelA} testid="compare-lap-a" color="#007AFF" label={t("lap_a")} />
            <button type="button" data-testid="compare-swap-btn" onClick={swap} className="justify-self-center p-2 border border-[#282D3D] rounded-sm text-slate-400 hover:text-white"><ArrowLeftRight className="w-4 h-4" /></button>
            <LapPicker items={itemsFor(selB)} value={selB} onChange={setSelB} testid="compare-lap-b" color="#FF3B30" label={t("lap_b")} />
            <div className="flex items-end gap-2 min-w-0">
              <div className="flex-1 min-w-0"><LapPicker items={itemsFor(selC)} value={selC} onChange={setSelC} testid="compare-lap-c" color="#22D3EE" label={t("lap_c")} /></div>
              {selC && <button type="button" data-testid="compare-lap-c-clear" onClick={() => setSelC(null)} title={t("remove")} className="h-10 px-2 border border-[#282D3D] rounded-sm text-slate-400 hover:text-[#FF3B30]"><X className="w-4 h-4" /></button>}
            </div>
          </div>
          {lapA ? (
            <TelemetryAnalysis key={trackId} lapA={lapA} lapB={lapB} lapC={lapC} channels={Object.keys(lapA.ch)} trackId={trackId} mapKey={`cmp-${trackId}`} />
          ) : <p data-testid="compare-pick-hint" className="font-mono text-xs text-slate-500">{t("compare_pick_hint")}</p>}
        </div>
      )}
    </div>
  );
}
